import ModalConfirmBid from "@/component/ModalConfirmBid";
import OutOfTurns from "@/component/popups/ui-1/OutOfTurns";
import { getTurnRemain } from "@/Redux/futures/account/actions";
import { getDailyPrizeStatus, postDailyPrizeGuess } from '@/Redux/futures/daily-reward/actions';
import { getHistoryBid } from "@/Redux/futures/rightWeb/actions";
import { curStateAccount } from "@/Redux/selector";
import { Toast } from "@/utils/helper";
import btnBgMaster from "@assets/web-v3-images/guess-price-btn-bg-master.png";
import btnBg from "@assets/web-v3-images/guess-price-btn-bg.png";
import coinIcon from "@assets/web-v3-images/icons/guess-price-coin-icon.png";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Confirmation from './popup/Confirmation';
import Successfully from './popup/Successfully';
import { toast } from 'react-toastify';
import { useLoginModal } from '@/contexts/LoginModalContext';

/* Luật hợp lệ của giá đoán, lấy đúng theo TabRunning (page/components/Bid/
   TabRunning.jsx): 1.000 ≤ giá ≤ 2.000.000 và chia hết cho 50.
   Chuỗi lỗi bid_page.warning có ghi thẳng 3 mốc này nên đổi số ở đây thì phải
   sửa cả en.json/my.json. */
const MIN_BID = 1_000;
const MAX_BID = 2_000_000;
const BID_STEP = 50;

/* Cùng một toastId cho mọi lỗi của card: bấm submit liên tục thì toast cũ được
   thay chỗ, không xếp chồng thành một cột. */
const ERROR_TOAST_ID = "guess-price-error";

/**
 * Card nhập giá đoán. Dùng lại nguyên luồng đặt giá của TabRunning: validate tại
 * chỗ -> ModalConfirmBid (chính modal này dispatch postBidProduct và reset input
 * qua setValueInput) -> các popup lỗi/hết lượt dùng chung component cũ.
 *
 * @param onShweHoursReward gọi khi lượt đoán vừa rồi được thưởng Shwe Hours (đủ
 *        10 lượt trong giờ vàng). Chỉ bắn SAU khi modal xác nhận đã đóng để hai
 *        popup không chồng nhau — Home dựng popup thưởng.
 */
const GuessPriceCard = ({ product, isMaster = false, onShweHoursReward }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectorAccount = useSelector(curStateAccount);
  const [value, setValue] = useState("");
  // Chỉ để tô viền đỏ input; nội dung lỗi đi bằng toast.
  const [hasError, setHasError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalNoBid, setOpenModalNoBid] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openSuccessfully, setOpenSuccessfully] = useState(false);
  const [textMsg, setTextMsg] = useState('');
  const { openLogin } = useLoginModal();
  const [dataTitle, setDataTitle] = useState(t("confirmation"));
  // Thưởng Shwe Hours kèm trong response đặt giá — giữ lại chờ modal xác nhận
  // đóng rồi mới bắn lên Home (xem handleConfirmModalToggle).
  const [pendingShweReward, setPendingShweReward] = useState(null);

  /* Tổng lượt còn lại = CỘNG DỒN turn của mọi gói trong remainTurn, không lấy
     một field duy nhất (giống TabRunning). */
  const bidTotal = useMemo(
    () =>
      (selectorAccount?.remainTurn ?? []).reduce(
        (total, pack) => total + (pack?.turn ?? 0),
        0,
      ),
    [selectorAccount?.remainTurn],
  );

  const isValueErr = useMemo(() => {
    const price = +value;
    return price < MIN_BID || price > MAX_BID || price % BID_STEP !== 0;
  }, [value]);

  // Hiển thị có phân tách hàng nghìn cho đúng định dạng tiền MMK (1,000,000);
  // state `value` vẫn giữ chuỗi số thô để validate và submit.
  const displayValue =
    value === "" ? "" : Number(value).toLocaleString("en-US");

  const handleChange = (e) => {
    // Chỉ nhận chữ số: giá phải là số nguyên chia hết cho 50, mà "1,000"/"1.5"
    // khi ép +value sẽ ra NaN/số lẻ và lọt qua validate sai.
    setValue(e.target.value.replace(/[^0-9]/g, ""));
    setHasError(false);
  };

  /* Lỗi chỉ bắn lúc submit, không validate realtime: mỗi ký tự gõ dở đều sai
     luật (vd "1" < 1.000) nên bắn theo onChange sẽ ra một tràng toast. */
  const failValidation = (message) => {
    setHasError(true);
    Toast.error(message, { toastId: ERROR_TOAST_ID });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value === "") return failValidation(t("bid_page.please_type_the_number"));
    setHasError(false);

    if (bidTotal <= 0) {
      // Hết lượt: vẫn ưu tiên báo lỗi input/đăng nhập trước, cuối cùng mới tới OutOfTurns
      setOpenConfirmation(false);
      if (!product) return;
      if (isValueErr) return failValidation(t("bid_page.warning"));
      if (!selectorAccount?.userInfo) return openLogin();
      setDataTitle(t("confirmation"));
      return setOpenModalNoBid(true);
    } else {
      // Còn lượt: check đủ điều kiện rồi mới mở Confirmation
      if (!product) return;
      if (isValueErr) return failValidation(t("bid_page.warning"));
      if (!selectorAccount?.userInfo) return openLogin();
      setDataTitle(t("confirmation"));
      setOpenConfirmation(true);
    }
  };

  /* FR-HOME-009: đặt giá THÀNH CÔNG (success + errorCode 0) thì nạp lại lịch sử
     đặt giá -> cập nhật cả ticker "just guessed" lẫn lịch sử của user (đều đọc
     từ store rightWeb.bidHistory). ModalConfirmBid gọi setDataApiBid(res) ở
     nhánh hoàn tất nên dùng làm hook, lọc lại theo errorCode ở đây. */
  const handleBidPlaced = (res) => {
    if (!(res?.success && +res?.data?.errorCode === 0)) return;

    /* Lượt đoán thứ 10 trong giờ vàng: response đặt giá đã kèm sẵn phần thưởng
       (cùng field `shweHoursReward` mà TabRunning dùng) nên không cần đợi
       getBidCountShweHour trả về mới biết. */
    if (res.data.shweHoursReward) setPendingShweReward(res.data.shweHoursReward);

    /* Đặt giá vừa tiêu một lượt: nạp lại remain-turn để TurnsPill trên
       HomeHeader (và bidTotal ngay trong card này) hiện đúng số còn lại.
       ModalConfirmBid chỉ refresh getCurrentUser + getBidCountShweHour, KHÔNG
       đụng tới account.remainTurn -> không tự dispatch ở đây thì pill giữ số cũ
       tới lúc reload trang. */
    dispatch(getTurnRemain({}));

    if (product?.cp_id) {
      dispatch(
        getHistoryBid({
          query: { current: 1, pageSize: 7, sort: "desc", cp_ids: product.cp_id },
        }),
      );
    }
  };

  /* ModalConfirmBid tự gọi setOpenModal(false) khi bấm OK/Cancel/ESC — chen vào
     đây để popup thưởng chỉ bật LÊN SAU khi modal xác nhận đã đóng. */
  const handleConfirmModalToggle = (next) => {
    setOpenModal(next);
    if (next || !pendingShweReward) return;
    onShweHoursReward?.(pendingShweReward);
    setPendingShweReward(null);
  };

  const afterDailyPrizeGuess = (data, isLoading, dataError) => {
    if (isLoading) return;

    if (dataError) {
      toast.error(dataError?.errorMessage);
      return;
    }

    if (data) {
      setTextMsg(data)
      setOpenSuccessfully(true)
      setOpenConfirmation(false)
      dispatch(getDailyPrizeStatus({})); // update lại api của page
      dispatch(getTurnRemain({})); // call để update lại turn ở page home
    }
  };

  const handleDailyPrizeGuess = async () => {
    const body = {
      guess_price: value,
      callback: afterDailyPrizeGuess,
    };
    await dispatch(postDailyPrizeGuess(body));
  }

  // Kích thước bên trong tính theo cqw so với bản design rộng 820px
  // (1cqw = 8.2px design) để card khớp tỷ lệ 820/205 từ sm trở lên; trên
  // mobile bỏ khoá aspect + clamp() sàn kích thước để card không bị bé quá.
  // Riêng chữ trong input giữ min 16px — nhỏ hơn iOS sẽ tự zoom khi focus.
  return (
    /* Wrapper chỉ để xếp lớp halo: halo phải là ANH EM đứng trước card chứ không
       phải con của card. Đặt nó làm con với -z-10 thì `isolate` của card tạo
       stacking context riêng, negative z-index không lùi được xuống dưới
       background của chính cha -> halo phủ lên mặt card. `isolate` ở đây để cả
       cụm không ảnh hưởng z-index phần tử khác trên trang; @container giữ nguyên
       bề rộng cũ nên mọi giá trị cqw bên trong không đổi. */
    <div className="@container isolate relative mt-4 animate-fade-in-up motion-reduce:animate-none">
      {/* Quầng sáng nhấp nháy ôm viền card. Mỗi lớp là một hình chữ nhật bo góc
          TRÙNG dáng card rồi blur (dùng -inset nhỏ chứ không phải gradient toả
          tròn, nhờ vậy quầng sáng bám đúng bo góc). Phần loang ra ngoài mép chính
          là halo; phần bên trong bị nền đục của card — vẽ sau, và có `relative`
          nên nằm trên các lớp absolute này — che hết.
          Gradient "sát viền #FCFCD9 -> càng xa càng ngả #EDE1A7" không viết được
          bằng linear-/radial-gradient (không có gradient nào đi theo khoảng cách
          tới một hình bo góc), nên dựng bằng 2 lớp: lớp #EDE1A7 loang rộng nằm
          dưới, lớp #FCFCD9 bám sát viền nằm trên đè lên phần gần card. Thứ tự DOM
          chính là thứ tự vẽ nên KHÔNG được đảo hai span này. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-[clamp(6px,1.4cqw,14px)] rounded-[clamp(22px,4.2cqw,36px)] bg-[#EDE1A7] blur-[clamp(16px,3.2cqw,32px)] animate-halo"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-[clamp(1px,0.3cqw,3px)] rounded-[clamp(19px,3.6cqw,30px)] bg-[#FFF] blur-[clamp(5px,1cqw,10px)] animate-halo"
      />

      <section className="relative sm:aspect-820/205 rounded-[clamp(18px,3.4cqw,28px)] border-[2px] border-transparent [background:linear-gradient(to_top,#F79009,#E62E05)_padding-box,linear-gradient(to_bottom,#EDE1A7,#EDE1A7,#EDE1A7)_border-box] px-[clamp(14px,2.4cqw,20px)] pb-[clamp(14px,2.4cqw,20px)] pt-[clamp(12px,3cqw,28px)] flex flex-col justify-between bit-container">
        <div className="flex items-center justify-center gap-2">
          <h2 className="relative whitespace-nowrap text-center text-[clamp(16px,4.5cqw,36px)] font-extrabold">
            <span
              aria-hidden="true"
              className="absolute inset-0 pr-[clamp(56px,10cqw,80px)] text-[#E04F16] [-webkit-text-stroke:0.5cqw_#E04F16] [text-shadow:0_0.25cqw_0.25cqw_#B93815]"
            >
              {t("home.guess.title")}
            </span>
            <span className="relative text-transparent bg-linear-to-b from-[#FBF5DA] to-[#FDEFB0] bg-clip-text pb-2.5">
              {t("home.guess.title")}
            </span>
          </h2>
          {/* Hộp quà tràn góc phải trên */}
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-[clamp(10px,1.5cqw,12px)] flex flex-col items-center gap-[clamp(8px,1.5cqw,12px)] bg-[#FDEAD7] px-[clamp(10px,2cqw,24px)] py-[clamp(16px,3.2cqw,32px)] rounded-[clamp(16px,3.2cqw,32px)]"
        >
          <label
            className={`flex h-[clamp(52px,11.3cqw,93px)] w-full flex-1 items-center gap-[1cqw] rounded-[16px] md:rounded-[24px] bg-white pl-[clamp(10px,1cqw,20px)] pr-[clamp(10px,2.4cqw,20px)]  py-2 ${hasError ? "outline outline-2 outline-[#F04438]" : ""
              }`}
          >
            <img
              src={coinIcon}
              alt=""
              width={56}
              height={56}
              className="size-[clamp(39px,8.5cqw,70px)] object-contain"
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={displayValue}
              onChange={handleChange}
              aria-invalid={hasError}
              className="w-full min-w-0 bg-transparent text-[max(28px,6cqw)] font-bold text-[#B93815] outline-none placeholder:text-[#d8c2b2]"
            />
            <span className="text-[max(28px,6cqw)] font-extrabold text-[#B93815]">
              MMK
            </span>
          </label>

          <button
            type="submit"
            style={{
              backgroundImage: `url(${isMaster ? btnBgMaster : btnBg})`,
            }}
            className="btn-click-active w-full aspect-256/93 h-[clamp(52px,11.3cqw,93px)] shrink-0 rounded-full bg-size-[100%_100%] bg-no-repeat text-[clamp(17px,4cqw,33px)] font-black shadow-[0_0.75cqw_1.7cqw_rgba(180,90,0,0.35)] disabled:opacity-60"
            disabled={!product}
          >
            {/* Stroke 3px ở lớp dưới = 1.5px lộ ra ngoài viền chữ (nửa trong bị lớp trắng che).
              Hai lớp xếp chồng bằng grid (cùng ô) để glyph khớp tuyệt đối. */}
            <span className="inline-grid">
              <span
                aria-hidden="true"
                style={{
                  color: isMaster ? "#D92D20" : "#DC6803",
                  WebkitTextStroke: `3px ${isMaster ? "#D92D20" : "#DC6803"}`,
                }}
                className="col-start-1 row-start-1 [text-shadow:0_0.25cqw_0.4cqw_rgba(200,120,0,0.7)]"
              >
                {t("home.guess.submit")}
              </span>
              <span className="col-start-1 row-start-1 text-white">
                {t("home.guess.submit")}
              </span>
            </span>
          </button>
        </form>

        {/* Hết lượt khi đặt giá -> popup Out of Turns (v3 PopupFrame). */}
        <OutOfTurns open={openModalNoBid} onOpenChange={setOpenModalNoBid} />
        {openModal ? (
          <ModalConfirmBid
            openModal={openModal}
            setOpenModal={handleConfirmModalToggle}
            valueInput={+value}
            data={[product]}
            setValueInput={setValue}
            dataTitle={dataTitle}
            setDataTitle={setDataTitle}
            // Chỉ TabRunning cần errorCode (để chạy animation icon bay), ở đây không dùng.
            setIsErrorCode={() => { }}
            // Hook đặt-giá-xong -> nạp lại ticker + lịch sử đặt giá (FR-HOME-009).
            setDataApiBid={handleBidPlaced}
          />
        ) : null}
      </section>

      {/* Blur riêng cho VIỀN card, không liên quan halo (không nhấp nháy, không
          dùng animate-halo). Là một vòng viền rỗng trùng khít card rồi blur, nên
          dải mờ toả đều ra cả hai phía của đường viền. Bắt buộc đứng SAU
          </section> để vẽ đè lên mặt card — nằm trước thì nửa trong của dải mờ bị
          nền card che, chỉ còn nửa ngoài và trông y như halo. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[clamp(18px,3.4cqw,28px)] border-[clamp(3px,0.6cqw,5px)] border-[#EDE1A7] blur-[clamp(2px,0.5cqw,5px)]"
      />

      <Confirmation
        open={openConfirmation}
        onOpenChange={setOpenConfirmation}
        handleClick={handleDailyPrizeGuess}
        value={displayValue}
        productName={product?.product_name}
      />
      <Successfully
        open={openSuccessfully}
        onOpenChange={setOpenSuccessfully}
        text={textMsg?.message}
      />
    </div>

  );
};

export default GuessPriceCard;
